import React, { useEffect } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useUserAnalyticsOverview } from '../../../hooks/api/useDashboard';
import { StatCard } from '../../molecules/StatCard';
import { recordToChartData, sumDistribution } from '../../../utils/distribution';
import type {
  AnalyticsDateRange,
  DataSource,
} from '../../../types/api/dashboard';

// ────────────────────────── Visual helpers ─────────────────────────

const TIER_COLORS: Record<string, string> = {
  FREE: '#9CA3AF',
  PREMIUM: '#EAB308',
  VIP: '#8B5CF6',
};

const PLATFORM_COLORS: Record<string, string> = {
  MOBILE: '#EAB308',
  TV: '#6366F1',
  WEB: '#14B8A6',
  IOS: '#0EA5E9',
  ANDROID: '#10B981',
};

const FALLBACK_PALETTE = [
  '#EAB308',
  '#8B5CF6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#6366F1',
  '#14B8A6',
];

function colorFor(map: Record<string, string>, key: string, fallbackIdx: number): string {
  return map[key] ?? FALLBACK_PALETTE[fallbackIdx % FALLBACK_PALETTE.length];
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return n.toLocaleString();
}

// ────────────────────────── Sub-components ─────────────────────────

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
    <h4 className="text-sm font-semibold text-gray-700 mb-4">{title}</h4>
    {children}
  </div>
);

const DonutTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; payload?: { name: string; value: number } }>;
  total: number;
}> = ({ active, payload, total }) => {
  if (!active || !payload?.length) return null;
  const datum = payload[0].payload ?? { name: payload[0].name ?? '', value: payload[0].value ?? 0 };
  const pct = total > 0 ? Math.round((datum.value / total) * 100) : 0;
  return (
    <div className="bg-gray-900/90 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg">
      <p className="font-medium">{datum.name}</p>
      <p>
        {datum.value.toLocaleString()} ({pct}%)
      </p>
    </div>
  );
};

const TrendTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900/90 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg">
      <p className="font-medium">{label}</p>
      <p>{(payload[0].value ?? 0).toLocaleString()} sign-ups</p>
    </div>
  );
};

const Donut: React.FC<{
  data: { name: string; value: number }[];
  colors: Record<string, string>;
  emptyLabel: string;
}> = ({ data, colors, emptyLabel }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (data.length === 0 || total === 0) {
    return <div className="h-56 flex items-center justify-center text-sm text-gray-400">{emptyLabel}</div>;
  }
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
            stroke="none"
          >
            {data.map((d, idx) => (
              <Cell key={d.name} fill={colorFor(colors, d.name, idx)} />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} />} />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ────────────────────────── Section ────────────────────────────────

export interface UsersSectionProps {
  range: AnalyticsDateRange;
  /** Lets the parent compute a unified Live/Mock badge across sections. */
  onSourceChange?: (source: DataSource) => void;
}

export const UsersSection: React.FC<UsersSectionProps> = ({ range, onSourceChange }) => {
  const { data, dataSource, isLoading, error } = useUserAnalyticsOverview(range);

  useEffect(() => {
    onSourceChange?.(dataSource);
  }, [dataSource, onSourceChange]);

  const tierData = recordToChartData(data?.tierDistribution, { skipZero: true });
  const platformData = recordToChartData(data?.platformDistribution, { skipZero: true });
  const tierTotal = sumDistribution(data?.tierDistribution);
  const platformTotal = sumDistribution(data?.platformDistribution);

  return (
    <section className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Users</h3>
        {tierTotal > 0 && (
          <p className="text-xs text-gray-500">
            Tier total: {tierTotal.toLocaleString()} &middot; Platform total:{' '}
            {platformTotal.toLocaleString()}
          </p>
        )}
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Failed to load user analytics.
        </div>
      ) : null}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          }
          iconBgColor="bg-[#EAB308]"
          title="Total Users"
          value={isLoading ? '…' : formatNumber(data?.totalUsers ?? 0)}
          trend="across all platforms"
          trendColor="text-gray-500"
        />
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          }
          iconBgColor="bg-emerald-500"
          title="New Users"
          value={isLoading ? '…' : formatNumber(data?.newUsers ?? 0)}
          trend="in selected range"
          trendColor="text-gray-500"
        />
        <StatCard
          icon={
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          }
          iconBgColor="bg-amber-500"
          title="Active Users"
          value={isLoading ? '…' : formatNumber(data?.activeUsers ?? 0)}
          trend="logged in during range"
          trendColor="text-gray-500"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Tier Distribution">
          <Donut data={tierData} colors={TIER_COLORS} emptyLabel="No tier data" />
        </SectionCard>

        <SectionCard title="Platform Distribution">
          <Donut data={platformData} colors={PLATFORM_COLORS} emptyLabel="No platform data" />
        </SectionCard>

        <SectionCard title="Registration Trend">
          {(data?.registrationTrend?.length ?? 0) === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-gray-400">
              No registrations in range
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.registrationTrend ?? []}>
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    tickFormatter={(v: string) => v.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6B7280' }}
                    allowDecimals={false}
                    width={32}
                  />
                  <Tooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(60,125,195,0.08)' }} />
                  <Bar dataKey="value" fill="#EAB308" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>
    </section>
  );
};

export default UsersSection;
